import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconChevronDown, IconSearch, IconX } from "./icons";
import { CheckBox, Input } from "./input";
import type { SelectOption } from "./select";
import { Tooltip } from "./tooltip";

const ITEM_HEIGHT = 40;
const CONTAINER_HEIGHT = 256;
const OVERSCAN = 5;
export const VIRTUALIZE_THRESHOLD = 50;

export function useVirtualList(itemCount: number) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      setScrollTop(scrollContainerRef.current.scrollTop);
    }
  }, []);

  const totalHeight = itemCount * ITEM_HEIGHT;
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN,
  );
  const visibleCount = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT) + 2 * OVERSCAN;
  const endIndex = Math.min(itemCount - 1, startIndex + visibleCount);

  const topPadding = startIndex * ITEM_HEIGHT;
  const bottomPadding = Math.max(0, (itemCount - endIndex - 1) * ITEM_HEIGHT);

  const virtualItems = useMemo(() => {
    const items: { index: number }[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      items.push({ index: i });
    }
    return items;
  }, [startIndex, endIndex]);

  return {
    virtualItems,
    topPadding,
    bottomPadding,
    totalHeight,
    handleScroll,
    scrollContainerRef,
  };
}

function OptionButton<V extends string = string>({
  option,
  checked,
  onToggle,
  renderOptionLabel,
  getOptionTitle,
  checkBoxVariant,
  checkBoxVariantSize,
  disabledOptionTooltip,
}: {
  option: SelectOption<V>;
  checked: boolean;
  onToggle: (v: V) => void;
  renderOptionLabel?: (option: SelectOption<V>) => React.ReactNode;
  getOptionTitle?: (option: SelectOption<V>) => string | undefined;
  checkBoxVariant: "default" | "simple";
  checkBoxVariantSize: "base" | "sm";
  disabledOptionTooltip?: string;
}) {
  const button = (
    <button
      key={(option.key as any) || (option.value as any)}
      type="button"
      title={getOptionTitle?.(option)}
      className={cn(
        "w-full",
        "rounded",
        "my-0.5",
        "px-2 py-2",
        "text-left",
        "hover:bg-gray-50",
        "focus:outline-none",
        "focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-inset",
        option.disabled
          ? "opacity-50 cursor-not-allowed hover:bg-transparent"
          : null,
      )}
      onClick={() => onToggle(option.value as V)}
      disabled={option.disabled}
    >
      <div className="pointer-events-none">
        <CheckBox
          label={renderOptionLabel ? renderOptionLabel(option) : option.label}
          checked={checked}
          onChange={() => onToggle(option.value as V)}
          variant={checkBoxVariant}
          variantSize={checkBoxVariantSize}
          disabled={option.disabled}
          labelClassName="ml-2"
        />
      </div>
    </button>
  );

  if (option.disabled && disabledOptionTooltip) {
    return (
      <Tooltip
        text={disabledOptionTooltip}
        placement="top"
        referenceClassName="block w-full cursor-not-allowed"
      >
        {button}
      </Tooltip>
    );
  }

  return button;
}

export interface MultiSelectProps<V extends string = string> {
  id?: string;
  options: SelectOption<V>[];
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  // Controlled values
  value?: V[];
  // Uncontrolled default values
  defaultValue?: V[];
  // Change handler
  onChange?: (values: V[], selectedOptions: SelectOption<V>[]) => void;
  // Controls
  showAllButton?: boolean;
  showSelectAllButton?: boolean;
  selectAllVisibleLimit?: number;
  disableSelectAll?: boolean;
  allLabel?: string;
  searchPlaceholder?: string;
  disableSearch?: boolean;
  renderOptionLabel?: (option: SelectOption<V>) => React.ReactNode;
  getOptionTitle?: (option: SelectOption<V>) => string | undefined;
  getTriggerDisplayText?: (selected: V[]) => string;
  dropdownClassName?: string;
  checkBoxVariant?: "default" | "simple";
  checkBoxVariantSize?: "base" | "sm";
  disableVirtualization?: boolean;
  customContent?: React.ReactNode;
  /** Tooltip text shown when hovering over a disabled option */
  disabledOptionTooltip?: string;
}

export function MultiSelect<V extends string = string>({
  id,
  options,
  className = "",
  ariaLabel = "combobox",
  disabled = false,
  placeholder = "Select",
  value,
  defaultValue,
  onChange,
  showAllButton = true,
  showSelectAllButton = true,
  selectAllVisibleLimit,
  disableSelectAll = false,
  allLabel = "All",
  searchPlaceholder = "Search...",
  disableSearch = false,
  renderOptionLabel,
  getOptionTitle,
  getTriggerDisplayText,
  dropdownClassName,
  checkBoxVariant = "default",
  checkBoxVariantSize = "base",
  disableVirtualization = false,
  customContent,
  disabledOptionTooltip,
}: MultiSelectProps<V>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState("");

  const isControlled = Array.isArray(value);
  const [internalSelected, setInternalSelected] = useState<V[]>(
    defaultValue ?? [],
  );
  const effectiveSelected =
    (isControlled ? (value as V[]) : internalSelected) || [];

  const isNoneSelected = effectiveSelected.length === 0;

  const triggerClassName = cn(
    "relative",
    "border",
    "border-gray-300 text-black",
    "hover:border-gray-500",
    "focus:ring-gray-500 focus:border-gray-500",
    "active:text-black",
    "disabled:bg-black-50 disabled:border-black-100 disabled:text-black",
    "rounded-md shadow-sm",
    "px-3 py-2 pr-10",
    "w-full text-left",
    className,
  );

  const filteredOptions = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (s === "") return options;
    return options.filter((o) => o.label.toLowerCase().includes(s));
  }, [options, search]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!containerRef.current) return;
    if (!next || !containerRef.current.contains(next)) {
      setOpen(false);
    }
  };
  const handleMouseDown = () => {
    // Keep focus anchored on the container so blur won't fire when clicking non-focusable children
    containerRef.current?.focus();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const clearSearch = () => {
    setSearch("");
    // Keep the user in the search input after clearing.
    searchInputRef.current?.focus();
  };

  const emitChange = (nextValues: V[]) => {
    const selectedOptions = options.filter((o) =>
      nextValues.some((v) => v === (o.value as any)),
    );
    onChange?.(nextValues, selectedOptions);
    if (!isControlled) {
      setInternalSelected(nextValues);
    }
  };

  const onSelectAll = () => {
    if (search.trim()) {
      const filteredValues = new Set(
        filteredOptions.filter((o) => !o.disabled).map((o) => o.value),
      );
      const combined = new Set([...effectiveSelected, ...filteredValues]);
      emitChange(Array.from(combined) as V[]);
    } else {
      const enabledValues = options
        .filter((o) => !o.disabled)
        .map((o) => o.value as V);
      emitChange(enabledValues);
    }
  };

  const onClear = () => {
    emitChange([]);
  };

  const onToggleOne = (v: V) => {
    const set = new Set(effectiveSelected as V[]);
    if (set.has(v)) {
      set.delete(v);
    } else {
      set.add(v);
    }
    emitChange(Array.from(set));
  };

  const triggerLabel = useMemo(() => {
    if (getTriggerDisplayText) return getTriggerDisplayText(effectiveSelected);
    if (isNoneSelected) return "";
    const count = effectiveSelected.length;
    if (count === 1) {
      const found = options.find(
        (o) => (o.value as any) === (effectiveSelected[0] as any),
      );
      return found ? found.label : "1 selected";
    }
    return `${count} selected`;
  }, [isNoneSelected, effectiveSelected, options]);

  const triggerDisplayText = useMemo(() => {
    return triggerLabel;
  }, [triggerLabel]);

  const shouldVirtualize =
    !disableVirtualization && filteredOptions.length >= VIRTUALIZE_THRESHOLD;
  const {
    virtualItems,
    topPadding,
    bottomPadding,
    handleScroll,
    scrollContainerRef,
  } = useVirtualList(shouldVirtualize ? filteredOptions.length : 0);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [search]);

  const enabledOptionCount = useMemo(
    () => options.filter((option) => !option.disabled).length,
    [options],
  );
  const enabledFilteredOptionCount = useMemo(
    () => filteredOptions.filter((option) => !option.disabled).length,
    [filteredOptions],
  );
  const hasActiveSearch = search.trim() !== "";
  const canShowSelectAll =
    showSelectAllButton &&
    (selectAllVisibleLimit === undefined
      ? true
      : hasActiveSearch
        ? enabledFilteredOptionCount <= selectAllVisibleLimit
        : enabledOptionCount <= selectAllVisibleLimit);

  const renderOptions = () => {
    if (filteredOptions.length === 0) {
      return <div className="text-sm text-gray-500 px-1 py-2">No results</div>;
    }

    if (shouldVirtualize) {
      return (
        <>
          {topPadding > 0 && <div style={{ height: topPadding }} aria-hidden />}
          {virtualItems.map(({ index }) => {
            const o = filteredOptions[index];
            const checked = effectiveSelected.some(
              (v) => v === (o.value as any),
            );
            return (
              <OptionButton
                key={(o.key as any) || (o.value as any)}
                option={o}
                checked={checked}
                onToggle={onToggleOne}
                renderOptionLabel={renderOptionLabel}
                getOptionTitle={getOptionTitle}
                checkBoxVariant={checkBoxVariant}
                checkBoxVariantSize={checkBoxVariantSize}
                disabledOptionTooltip={disabledOptionTooltip}
              />
            );
          })}
          {bottomPadding > 0 && (
            <div style={{ height: bottomPadding }} aria-hidden />
          )}
        </>
      );
    }

    return filteredOptions.map((o) => {
      const checked = effectiveSelected.some((v) => v === (o.value as any));
      return (
        <OptionButton
          key={(o.key as any) || (o.value as any)}
          option={o}
          checked={checked}
          onToggle={onToggleOne}
          renderOptionLabel={renderOptionLabel}
          getOptionTitle={getOptionTitle}
          checkBoxVariant={checkBoxVariant}
          checkBoxVariantSize={checkBoxVariantSize}
          disabledOptionTooltip={disabledOptionTooltip}
        />
      );
    });
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      tabIndex={-1}
      onBlur={handleBlur}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
    >
      <button
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerClassName}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="block truncate">
          {triggerDisplayText || placeholder}
        </span>
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-700">
          <IconChevronDown variant="sm" />
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          aria-multiselectable
          className={cn(
            "absolute z-10 mt-1 min-w-full",
            "bg-white border border-gray-200 rounded-md shadow-lg",
            "p-2",
            dropdownClassName,
          )}
        >
          {!disableSearch && (
            <>
              <div className="relative">
                <IconSearch
                  className="absolute top-[10px] left-[10px]"
                  color="#595E63"
                  variant="sm"
                />
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  className="pl-9 pr-9 w-full"
                  autoFocus
                  ref={searchInputRef}
                />
                {search.trim() !== "" ? (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className={cn(
                      "absolute",
                      "top-1/2 -translate-y-1/2 right-[10px]",
                      "text-gray-500 hover:text-gray-700",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-inset rounded",
                    )}
                    onClick={clearSearch}
                  >
                    <IconX variant="sm" color="#6B7280" />
                  </button>
                ) : null}
              </div>
              <div className="h-px bg-gray-200 my-2" />
            </>
          )}
          {showAllButton ? (
            <div
              className={cn(
                "flex items-center px-2 py-2",
                canShowSelectAll ? "justify-between" : "justify-end",
              )}
            >
              {canShowSelectAll ? (
                <button
                  type="button"
                  className={cn(
                    "text-sm font-medium",
                    disabled || disableSelectAll
                      ? "text-indigo-200 cursor-not-allowed"
                      : "text-indigo-400 hover:text-gray-600",
                  )}
                  onClick={onSelectAll}
                  disabled={disabled || disableSelectAll}
                >
                  Select All
                </button>
              ) : null}
              <button
                type="button"
                className={cn(
                  "text-sm font-medium",
                  disabled
                    ? "text-indigo-200 cursor-not-allowed"
                    : "text-indigo-400 hover:text-indigo-600",
                )}
                onClick={onClear}
                disabled={disabled}
              >
                Clear
              </button>
            </div>
          ) : null}
          {customContent ? (
            <>
              <div className="h-px bg-gray-200 my-2" />
              <div className="px-2 py-2">{customContent}</div>
            </>
          ) : null}
          <div className="h-px bg-gray-200 my-2" />
          <div
            ref={scrollContainerRef}
            onScroll={shouldVirtualize ? handleScroll : undefined}
            className="max-h-64 overflow-auto pr-1 pl-2 py-1"
          >
            {renderOptions()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
